import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useActivityTrigger} from 'hooks/useActivityTrigger'
import {
  getPriceAlertPromptVisibility,
  hidePriceAlertPrompt,
  showPriceAlertPrompt
} from 'modules/priceAlertPrompt/slice'
import {getOfferEntity} from 'modules/sapiSearch/selectors'
import {getCheapestTopOfferByNightlyRate} from 'utils/offers'
import {
  setPriceAlertPromptShown,
  wasPriceAlertPromptShownToday
} from 'utils/persistence'

import {useIsBlockingSurfaceOpen} from '@vio/atlas/src/context/blockingSurfaces'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'

import {PRICE_ALERT_PROMPT_SURFACE} from './constants'
import {trackPriceAlertPromptDisplayed} from './tracking'
import {useIsPriceWatchOn} from './useIsPriceWatchOn'
import {usePriceAlertPromptEligibility} from './usePriceAlertPromptEligibility'

import type {ContentHotel, Hotel} from '@findhotel/sapi'

interface UsePriceAlertPromptResult {
  isOpen: boolean
  /** Close after accept. */
  hide: () => void
  /** Close via the "Maybe later" action. */
  dismiss: () => void
}

/**
 * Lifecycle for the proactive AP price-alert prompt (#186392). Mount with
 * `key={hotelId}` so the dwell timer restarts per hotel. SSR-safe: the Price Watch
 * action hook only runs inside the content, which renders once the prompt is open.
 */
export const usePriceAlertPrompt = (
  hotel: Hotel | ContentHotel | undefined
): UsePriceAlertPromptResult => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()

  const hotelId = hotel?.objectID

  const isOpen = useSelector(getPriceAlertPromptVisibility)
  const isConflictingSurfaceOpen = useIsBlockingSurfaceOpen(
    PRICE_ALERT_PROMPT_SURFACE
  )
  const isPriceWatchAlreadyOn = useIsPriceWatchOn(hotel)
  const isEligible = usePriceAlertPromptEligibility(hotelId)
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))

  const cheapestOffer = getCheapestTopOfferByNightlyRate(offerEntity?.offers)

  // Ready once the hotel + a priced offer are loaded, so the body shows a real price.
  const isReady = Boolean(hotel && cheapestOffer)

  // Read the day cap on mount to avoid showing then flickering away.
  const [wasShownToday] = useState(() => wasPriceAlertPromptShownToday())
  const hasTriggeredRef = useRef(false)

  const enabled = Boolean(
    hotelId &&
      isEligible &&
      isReady &&
      !isPriceWatchAlreadyOn &&
      !isConflictingSurfaceOpen &&
      !wasShownToday &&
      !isOpen
  )

  const onTrigger = useCallback(() => {
    if (hasTriggeredRef.current) return
    // Re-check at fire-time: another AP (or tab) may have fired during the dwell.
    if (wasPriceAlertPromptShownToday()) return
    if (isConflictingSurfaceOpen) return

    hasTriggeredRef.current = true
    setPriceAlertPromptShown()

    dispatch(showPriceAlertPrompt())
    dispatchTeamEvent(trackPriceAlertPromptDisplayed({hotelId}))
  }, [dispatch, dispatchTeamEvent, hotelId, isConflictingSurfaceOpen])

  // Fires after 30s on the AP; idle trigger disabled so a short pause never shows it.
  useActivityTrigger({enabled, onTrigger, idleMs: null})

  // Yield to any conflicting surface opened while we're showing.
  useEffect(() => {
    if (isOpen && isConflictingSurfaceOpen) {
      dispatch(hidePriceAlertPrompt())
    }
  }, [isOpen, isConflictingSurfaceOpen, dispatch])

  const close = useCallback(() => {
    dispatch(hidePriceAlertPrompt())
  }, [dispatch])

  return {isOpen, hide: close, dismiss: close}
}
